import {
  Fragment,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import type { PluginHeaderBreadcrumbEntry } from '@backstage/ui';
import { BreadcrumbEntry, useRouteRefParams } from '@backstage/frontend-plugin-api';
import { RELATION_PART_OF } from '@backstage/catalog-model';
import {
  entityRouteRef,
  getEntityRelations,
  useAsyncEntity,
  useEntityPresentation,
  useEntityRefLink,
} from '@backstage/plugin-catalog-react';
import type { EntityHeaderLayoutProps } from '@backstage/plugin-catalog-react/alpha';
import { AccountHeaderActions } from './AccountHeaderActions';
import { AccountSettingsPanel } from './AccountSettingsPanel';
import {
  AccountEntity,
  getAccountSyncState,
} from '@internal/backstage-plugin-catalog-devops-common';
import { convertDateFormat } from '@internal/backstage-plugin-devops-react';
import { EntityChip } from './EntityChip';
import { HeaderRegions } from './HeaderRegions';
import { HeaderTabs } from './HeaderTabs';
import styles from './AccountPageHeader.module.css';

/** Registers a trail for the breadcrumb-only plugin header above the page. */
export const BreadcrumbTrail = ({
  entries,
  children,
}: {
  entries?: PluginHeaderBreadcrumbEntry[];
  children: ReactNode;
}) => {
  if (!entries || entries.length === 0) {
    return <Fragment>{children}</Fragment>;
  }
  const [{ label, href }, ...rest] = entries;
  return (
    <BreadcrumbEntry entry={{ label, href }}>
      <BreadcrumbTrail entries={rest}>{children}</BreadcrumbTrail>
    </BreadcrumbEntry>
  );
};

/** 'group:default/team-x' -> 'team-x' for the owner chip. */
const shortOwnerRef = (ownerRef: string): string => {
  const withoutKind = ownerRef.includes(':')
    ? ownerRef.split(':').pop()!
    : ownerRef;
  return withoutKind.includes('/')
    ? withoutKind.split('/').pop()!
    : withoutKind;
};

const AccountPageHeader = ({ tabs, activeTabId }: EntityHeaderLayoutProps) => {
  const { entity } = useAsyncEntity();
  const routeParams = useRouteRefParams(entityRouteRef);
  const entityLink = useEntityRefLink();
  const presentation = useEntityPresentation(entity ?? routeParams);
  const headerRef = useRef<HTMLDivElement>(null);
  const account = entity as AccountEntity | undefined;

  // Trail shown in the plugin header: Catalog > <domain> > <this account>.
  // Registered from the first render so the header shows the trail
  // immediately, with the entity crumbs added once loaded.
  const breadcrumbs = useMemo(() => {
    const trail: PluginHeaderBreadcrumbEntry[] = [];
    if (account) {
      for (const domain of getEntityRelations(account, RELATION_PART_OF, {
        kind: 'domain',
      })) {
        trail.push({ label: domain.name, href: entityLink(domain) });
      }
      trail.push({
        label: account.metadata.title ?? account.metadata.name,
        href: entityLink(account),
      });
    }
    return trail;
  }, [account, entityLink]);

  const syncState = getAccountSyncState(account);
  const specType = account?.spec.type;
  const regionNames = account?.spec.regions?.map(region => region.Name) ?? [];
  const subtitleSegments: ReactNode[] = [];
  if (account) {
    subtitleSegments.push(`AWS account ${account.metadata.name}`);
    if (regionNames.length > 0) {
      subtitleSegments.push(
        <HeaderRegions
          accountName={account.metadata.title ?? account.metadata.name}
          regions={regionNames}
        />,
      );
    }
    const lastScan = convertDateFormat(account.spec.lastScanTime);
    if (lastScan) {
      subtitleSegments.push(`last scan ${lastScan}`);
    }
  }

  return (
    <BreadcrumbTrail entries={breadcrumbs}>
      <div ref={headerRef} className={styles.root}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{presentation.primaryTitle}</h1>
          {account && (
            <>
              <EntityChip tone="info">{account.kind}</EntityChip>
              {specType && (
                <EntityChip tone="neutral">
                  {specType.startsWith('AWS') ? `☁ ${specType}` : specType}
                </EntityChip>
              )}
              {account.spec.owner && (
                <EntityChip tone="neutral">
                  {`Owner: ${shortOwnerRef(account.spec.owner)}`}
                </EntityChip>
              )}
              <EntityChip tone={syncState.tone}>
                {`● ${syncState.label}`}
              </EntityChip>
            </>
          )}
          <div className={styles.actions}>
            <AccountHeaderActions />
          </div>
        </div>
        {subtitleSegments.length > 0 && (
          <p className={styles.subtitle}>
            {subtitleSegments.map((segment, index) => (
              <Fragment key={index}>
                {index > 0 && ' · '}
                {segment}
              </Fragment>
            ))}
          </p>
        )}
        <HeaderTabs tabs={tabs} activeTabId={activeTabId} />
        <AccountSettingsPanel anchorRef={headerRef} />
      </div>
    </BreadcrumbTrail>
  );
};

export { AccountPageHeader };
